import { LDateTime } from '@app/components/date/LDateTime';
import { ScheduledAodRunDto } from '@app/modules/audit/modules/aod/aod.types';
import { toDateTimeStr } from '@mi-front/core';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import { IconButton, Tooltip } from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import { Link } from 'react-router-dom';
import { LDate } from '../../../../../../components/date/LDate';
import { $T } from '../../../../../../locale/types';
import { aodRoutes } from '../../routing/routes';
import { AodScheduledRunActions } from '../AodScheduledRunActions';
import { AodTriggerType } from '../AodTriggerType';

export interface ScheduledRunsColumnsOptions {
  includeType?: boolean;
  includeActions?: boolean;
  includeName?: boolean;
  includeRuleLink?: boolean;
}

export const createScheduledRunsColumns = (
  $t: $T,
  options: ScheduledRunsColumnsOptions = {},
): MRT_ColumnDef<ScheduledAodRunDto>[] => {
  const {
    includeType = true,
    includeActions = true,
    includeName = false,
    includeRuleLink = true,
  } = options;

  const columns: MRT_ColumnDef<ScheduledAodRunDto>[] = [
    {
      id: 'scheduledAt',
      header: $t({
        id: 'audit.aod.runs.scheduledAt',
        defaultMessage: 'Programada',
      }),
      accessorFn: (row) => row.scheduledAt,
      Cell: ({ row: { original } }) => (
        <LDateTime value={toDateTimeStr(original.scheduledAt)} showRelative />
      ),
      size: 120,
      enableColumnFilter: false,
      enableGlobalFilter: false,
      enableSorting: true,
    },
  ];

  // Name column
  if (includeName) {
    columns.push({
      id: 'name',
      header: $t({
        id: 'audit.aod.runs.name',
        defaultMessage: 'Nombre',
      }),
      accessorFn: (row) => row.data.name,
      Cell: ({ row: { original } }) => original.data.name || '',
      size: 150,
      enableColumnFilter: true,
      filterVariant: 'text',
      filterFn: 'contains',
      enableSorting: true,
    });
  }

  if (includeType) {
    columns.push({
      id: 'type',
      header: $t({
        id: 'audit.aod.runs.type',
        defaultMessage: 'Trigger',
      }),
      accessorFn: (row) => row.data.runType,
      Cell: ({ row: { original } }) => <AodTriggerType type={original.data.runType} size="small" />,
      size: 120,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    });
  }

  // Order column
  columns.push({
    id: 'order',
    header: $t({
      id: 'audit.aod.runs.order',
      defaultMessage: 'Orden',
    }),
    accessorFn: (row) => row.data.order,
    Cell: ({ row: { original } }) => original.data.order,
    size: 100,
    enableColumnFilter: true,
    filterVariant: 'text',
    filterFn: 'contains',
    enableSorting: true,
  });

  // Interval column
  columns.push({
    id: 'interval',
    header: $t({
      id: 'audit.aod.runs.interval',
      defaultMessage: 'Intervalo',
    }),
    accessorFn: (row) => row.data.interval,
    Cell: ({ row: { original } }) => {
      if (!original.data.interval) return '';

      // Split the interval by slash and format each date using LDate
      const dates = original.data.interval.split('/');
      if (dates.length !== 2) return original.data.interval;

      return (
        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
          <LDate value={toDateTimeStr(dates[0])} day="2-digit" month="2-digit" />
          <span>-</span>
          <LDate value={toDateTimeStr(dates[1])} day="2-digit" month="2-digit" />
        </div>
      );
    },
    size: 120,
    enableColumnFilter: false,
  });

  // Actions column
  if (includeActions) {
    columns.push({
      id: 'actions',
      header: $t({
        id: 'common.text.actions',
        defaultMessage: 'Acciones',
      }),
      accessorFn: (row) => row.jobId,
      Cell: ({ row: { original } }) => {
        const ruleId = original.data.ruleId;
        if (!ruleId) return null;
        return <AodScheduledRunActions scheduledRun={original} ruleId={ruleId} />;
      },
      size: 120,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    });
  }

  // Rule link column
  if (includeRuleLink) {
    columns.push({
      id: 'rule',
      header: $t({
        id: 'common.actions',
        defaultMessage: 'Acciones',
      }),
      accessorFn: (row) => row.data.ruleId,
      Cell: ({ row: { original } }) => {
        const ruleId = original.data.ruleId;
        if (!ruleId) return null;
        return (
          <Tooltip
            title={$t({
              id: 'audit.aod.runs.actions.viewRule',
              defaultMessage: 'Ir a regla',
            })}
          >
            <IconButton
              component={Link}
              to={aodRoutes.ruleView.gen(ruleId)}
              size="small"
              color="primary"
            >
              <ManageHistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
      size: 80,
      enableColumnFilter: false,
      enableGlobalFilter: false,
      enableSorting: false,
    });
  }

  return columns;
};
